export type SetupRequirement = {
  id?: string;
  type?: string;
  provider?: string;
  provider_label?: string;
  required_env?: string;
  title?: string;
  description?: string;
  required?: boolean;
  satisfied?: boolean;
  action?: string;
};

export type CanonicalCategory = {
  id: string;
  label: string;
  collection: string;
  description: string;
};

export type ToolLike = {
  name?: string;
  display_name?: string;
  description?: string;
  display_description?: string;
  category?: string | null;
  tags?: string[];
  provider?: string | null;
  provider_label?: string | null;
  auth_required?: boolean | null;
  keys_schema?: any[];
  internal_env_vars?: any[];
  invocation_config?: Record<string, any> | null;
  canonical_category?: CanonicalCategory | null;
  collection?: string | null;
  capability_tags?: string[];
  setup_requirements?: SetupRequirement[];
};

export const CANONICAL_CATEGORIES: Record<string, CanonicalCategory> = {
  google_workspace: {
    id: "google_workspace",
    label: "Google Workspace",
    collection: "Workspace Suites",
    description: "Gmail, Drive, Docs, Sheets, Slides, Calendar, and Google admin tools.",
  },
  microsoft_365: {
    id: "microsoft_365",
    label: "Microsoft 365",
    collection: "Workspace Suites",
    description: "Outlook, OneDrive, SharePoint, Teams, Word, Excel, PowerPoint, and Entra tools.",
  },
  communication: {
    id: "communication",
    label: "Communication",
    collection: "Communication",
    description: "Messaging, email, chat, voice, SMS, and collaboration tools.",
  },
  productivity: {
    id: "productivity",
    label: "Productivity",
    collection: "Productivity & Office",
    description: "Documents, spreadsheets, presentations, PDFs, summaries, and office automation.",
  },
  marketing: {
    id: "marketing",
    label: "Marketing",
    collection: "Marketing & Social",
    description: "Campaign, copy, social publishing, advertising, and brand workflow tools.",
  },
  media_creative: {
    id: "media_creative",
    label: "Media & Creative",
    collection: "Creative Studio",
    description: "Image, video, audio, storyboard, captioning, and creative production tools.",
  },
  data_analytics: {
    id: "data_analytics",
    label: "Data & Analytics",
    collection: "Data & Analytics",
    description: "Data cleaning, reporting, charts, tables, reconciliation, and analytics tools.",
  },
  files_storage: {
    id: "files_storage",
    label: "Files & Storage",
    collection: "Knowledge & Files",
    description: "File upload, download, export, sharing, metadata, and storage tools.",
  },
  sales: {
    id: "sales",
    label: "Sales",
    collection: "Revenue Operations",
    description: "CRM, sales outreach, quote generation, and revenue workflow tools.",
  },
  operations: {
    id: "operations",
    label: "Operations",
    collection: "Operations",
    description: "SOPs, policies, tasks, approvals, and operational coordination tools.",
  },
  research: {
    id: "research",
    label: "Research",
    collection: "Research & Knowledge",
    description: "Search, monitor, summarize, and synthesize knowledge from external sources.",
  },
  finance_legal: {
    id: "finance_legal",
    label: "Finance & Legal",
    collection: "Finance & Legal",
    description: "Contracts, invoices, compliance, legal drafts, and financial documents.",
  },
  support: {
    id: "support",
    label: "Support",
    collection: "Customer Operations",
    description: "Customer support, response, and service workflow tools.",
  },
  developer_automation: {
    id: "developer_automation",
    label: "Developer & Automation",
    collection: "Developer & Automation",
    description: "Automation, scripts, integrations, webhooks, and developer productivity tools.",
  },
  other: {
    id: "other",
    label: "Other",
    collection: "Other",
    description: "General purpose tools that do not fit another catalog bucket.",
  },
};

export const CATEGORY_OPTIONS = Object.values(CANONICAL_CATEGORIES);

const CATEGORY_ALIASES: Record<string, string> = {
  ai_tool: "media_creative",
  advertising: "marketing",
  audio: "media_creative",
  data_fetcher: "data_analytics",
  data_processor: "data_analytics",
  data_processing: "data_analytics",
  data_writer: "data_analytics",
  file_operations: "files_storage",
  finance: "finance_legal",
  google: "google_workspace",
  google_workspace: "google_workspace",
  image_generation: "media_creative",
  integration: "developer_automation",
  legal: "finance_legal",
  marketing: "marketing",
  microsoft: "microsoft_365",
  microsoft_365: "microsoft_365",
  office: "productivity",
  operations: "operations",
  others: "other",
  productivity: "productivity",
  research: "research",
  sales: "sales",
  slack: "communication",
  social: "marketing",
  support: "support",
  utility: "developer_automation",
  video: "media_creative",
  video_generation: "media_creative",
};

const PROVIDER_LABELS: Record<string, string> = {
  adobe: "Adobe",
  box: "Box",
  buffer: "Buffer",
  calendly: "Calendly",
  discord: "Discord",
  dropbox: "Dropbox",
  facebook: "Facebook",
  frameio: "Frame.io",
  google: "Google",
  hootsuite: "Hootsuite",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  mailgun: "Mailgun",
  microsoft: "Microsoft",
  notion: "Notion",
  pinterest: "Pinterest",
  reddit: "Reddit",
  sendgrid: "SendGrid",
  slack: "Slack",
  telegram: "Telegram",
  tiktok: "TikTok",
  twilio: "Twilio",
  webex: "Webex",
  whatsapp: "WhatsApp",
  x: "X",
  youtube: "YouTube",
  zoom: "Zoom",
};

const INTERNAL_PROVIDER_IDS = new Set(["", "next_moca", "nextmoca", "local", "internal"]);

export function slug(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toolText(tool: ToolLike): string {
  return [
    tool.name,
    tool.display_name,
    tool.description,
    tool.display_description,
    tool.category,
    ...(tool.tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function inferProvider(tool: ToolLike): string {
  const explicit = slug(tool.provider || tool.invocation_config?.catalog_metadata?.provider);
  if (explicit) return explicit;
  const text = toolText(tool);
  const candidates: Record<string, string[]> = {
    google: ["google_", "gmail", "google drive", "google docs", "google sheets"],
    microsoft: ["microsoft_", "outlook", "sharepoint", "onedrive", "teams"],
    slack: ["slack_", "slack "],
    linkedin: ["linkedin_", "linkedin "],
    facebook: ["facebook_", "meta ads", "meta_"],
    instagram: ["instagram_", "instagram "],
    youtube: ["youtube_", "youtube "],
    x: ["x_twitter", "twitter"],
    telegram: ["telegram_", "telegram "],
    twilio: ["twilio_", "twilio "],
    zoom: ["zoom_", "zoom "],
    webex: ["webex_", "webex "],
    discord: ["discord_", "discord "],
    reddit: ["reddit_", "reddit "],
    pinterest: ["pinterest_", "pinterest "],
    tiktok: ["tiktok_", "tiktok "],
    whatsapp: ["whatsapp_", "whatsapp "],
    calendly: ["calendly_", "calendly "],
    buffer: ["buffer_", "buffer "],
    hootsuite: ["hootsuite_", "hootsuite "],
    sendgrid: ["sendgrid_", "sendgrid "],
    mailgun: ["mailgun_", "mailgun "],
  };
  for (const [provider, needles] of Object.entries(candidates)) {
    if (needles.some((needle) => text.includes(needle))) return provider;
  }
  return "next_moca";
}

export function normalizeCategory(tool: ToolLike): CanonicalCategory {
  if (tool.canonical_category?.id && CANONICAL_CATEGORIES[tool.canonical_category.id]) {
    return CANONICAL_CATEGORIES[tool.canonical_category.id];
  }
  const provider = inferProvider(tool);
  let key = CATEGORY_ALIASES[slug(tool.category || "")];
  if (provider === "google") key = "google_workspace";
  if (provider === "microsoft") key = "microsoft_365";
  if (["slack", "discord", "telegram", "twilio", "whatsapp", "webex"].includes(provider)) {
    key = key || "communication";
  }
  if (!key) {
    const text = toolText(tool);
    if (["gmail", "google_", "google "].some((token) => text.includes(token))) key = "google_workspace";
    else if (["microsoft_", "outlook", "sharepoint", "teams", "onedrive"].some((token) => text.includes(token))) key = "microsoft_365";
    else if (["image", "video", "audio", "caption", "storyboard"].some((token) => text.includes(token))) key = "media_creative";
    else if (["sheet", "csv", "chart", "analytics", "dashboard", "report"].some((token) => text.includes(token))) key = "data_analytics";
    else if (["drive", "dropbox", "box", "file", "folder", "storage"].some((token) => text.includes(token))) key = "files_storage";
    else if (["slack", "email", "message", "chat", "sms"].some((token) => text.includes(token))) key = "communication";
    else if (["social", "linkedin", "instagram", "facebook", "twitter", "x_", "youtube", "tiktok", "ad"].some((token) => text.includes(token))) key = "marketing";
    else if (["contract", "invoice", "sow", "legal", "policy"].some((token) => text.includes(token))) key = "finance_legal";
  }
  return CANONICAL_CATEGORIES[key || "other"];
}

function requirementId(prefix: string, name: string): string {
  return `${prefix}:${slug(name) || "credential"}`;
}

export function buildSetupRequirements(tool: ToolLike): SetupRequirement[] {
  if (Array.isArray(tool.setup_requirements)) return tool.setup_requirements;
  const embedded = tool.invocation_config?.catalog_metadata?.setup_requirements;
  if (Array.isArray(embedded)) return embedded;

  const provider = inferProvider(tool);
  const providerLabel =
    tool.provider_label ||
    PROVIDER_LABELS[provider] ||
    (INTERNAL_PROVIDER_IDS.has(provider) ? "Next Moca" : provider.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()));
  const requirements: SetupRequirement[] = [];
  for (const item of tool.keys_schema || []) {
    if (!item?.name) continue;
    const keyType = String(item.key_type || "api_key").toLowerCase();
    const isOauth = keyType.startsWith("oauth");
    requirements.push({
      id: requirementId(isOauth ? "oauth" : "api_key", item.name),
      type: isOauth ? "oauth" : "api_key",
      provider,
      provider_label: providerLabel,
      required_env: item.name,
      title: isOauth ? `Connect ${providerLabel}` : `Add ${providerLabel} API key`,
      description:
        item.description ||
        (isOauth
          ? `Authorize Next Moca with ${providerLabel} before this tool can call provider APIs.`
          : `Store ${item.name} in Key Manager before attaching this tool to an agent.`),
      required: item.required !== false,
      action: isOauth ? "connect_oauth" : "add_api_key",
    });
  }
  for (const item of tool.internal_env_vars || []) {
    if (!item?.name) continue;
    requirements.push({
      id: requirementId("env", item.name),
      type: "environment",
      provider,
      provider_label: providerLabel,
      required_env: item.name,
      title: `Configure ${item.name}`,
      description: item.description || `Set ${item.name} in the deployment environment.`,
      required: item.required !== false,
      action: "set_environment_variable",
    });
  }
  if (tool.auth_required && requirements.length === 0 && !INTERNAL_PROVIDER_IDS.has(provider)) {
    requirements.push({
      id: requirementId("oauth", provider),
      type: "oauth",
      provider,
      provider_label: providerLabel,
      title: `Connect ${providerLabel}`,
      description: `Authorize Next Moca with ${providerLabel} before this tool can run.`,
      required: true,
      action: "connect_oauth",
    });
  }
  return requirements;
}

export function getToolCatalogMetadata(tool: ToolLike) {
  const category = normalizeCategory(tool);
  const provider = inferProvider(tool);
  const providerLabel =
    tool.provider_label ||
    PROVIDER_LABELS[provider] ||
    (INTERNAL_PROVIDER_IDS.has(provider) ? "Next Moca" : provider.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()));
  return {
    provider,
    providerLabel,
    canonicalCategory: category,
    collection: tool.collection || category.collection,
    setupRequirements: buildSetupRequirements(tool),
    capabilityTags: tool.capability_tags || [],
  };
}

export function getRequirementLabel(requirement: SetupRequirement): string {
  if (requirement.type === "oauth") return "OAuth";
  if (requirement.type === "api_key") return "API key";
  if (requirement.type === "environment") return "Environment";
  return requirement.type || "Setup";
}

export function groupToolsByCollection<T extends ToolLike>(tools: T[]) {
  const groups = new Map<string, { name: string; count: number; tools: T[]; categories: Map<string, CanonicalCategory>; setupCount: number }>();
  for (const tool of tools) {
    const metadata = getToolCatalogMetadata(tool);
    const name = metadata.collection;
    const existing = groups.get(name) || {
      name,
      count: 0,
      tools: [],
      categories: new Map<string, CanonicalCategory>(),
      setupCount: 0,
    };
    existing.count += 1;
    existing.tools.push(tool);
    existing.categories.set(metadata.canonicalCategory.id, metadata.canonicalCategory);
    existing.setupCount += metadata.setupRequirements.length;
    groups.set(name, existing);
  }
  return Array.from(groups.values())
    .map((group) => ({ ...group, categories: Array.from(group.categories.values()) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
